import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { Workspace } from '../types/workspace';

type WorkspacesResponse = { data: Workspace[]; meta: { total: number } };
type WorkspaceResponse = Workspace;

type DefaultError = unknown;

export const workspaceQueryKeys = {
  all: ['workspaces'] as const,
  lists: () => [...workspaceQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...workspaceQueryKeys.lists(), params] as const,
  details: () => [...workspaceQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...workspaceQueryKeys.details(), id] as const,
};

type UseWorkspacesParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useWorkspaces<
  TData = WorkspacesResponse,
  TError = DefaultError,
>(
  { searchParams = {}, enabled = true }: UseWorkspacesParams = {},
  options?: Omit<
    UseQueryOptions<WorkspacesResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = workspaceQueryKeys.list(searchParams);

  return useQuery<WorkspacesResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getWorkspaces(searchParams),
    ...(options as any),
  });
}

type UseWorkspaceParams = {
  id?: string;
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useWorkspace<TData = WorkspaceResponse, TError = DefaultError>(
  { id, searchParams = {}, enabled = true }: UseWorkspaceParams = {},
  options?: Omit<
    UseQueryOptions<WorkspaceResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = workspaceQueryKeys.detail(id);

  return useQuery<WorkspaceResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => {
      if (!id) {
        throw new Error('Workspace ID is required');
      }
      return service.getWorkspace(id, searchParams);
    },
    ...(options as any),
  });
}
